import React, { useCallback, useEffect, useState } from "react";
import { useAdminApi } from "./useAdminApi";

const BUCKETS = ["loose", "cib", "new", "graded", "box", "manual"];

function formatPrice(bucket) {
  if (!bucket || bucket.price == null) return "—";
  return `$${Number(bucket.price).toFixed(2)}`;
}

function EvidenceList({ evidence }) {
  if (!evidence || evidence.length === 0) return null;
  return (
    <ul className="admin-evidence-list">
      {evidence.map((e, i) => (
        <li className="admin-evidence-item" key={i}>
          <a
            href={e.url}
            target="_blank"
            rel="noreferrer"
            className="admin-evidence-title"
            title={e.title}
          >
            {e.title}
          </a>
          <span className="admin-evidence-price">${Number(e.price).toFixed(2)}</span>
        </li>
      ))}
    </ul>
  );
}

function PriceBucket({ name, bucket, showEvidence }) {
  return (
    <div className="admin-price-bucket">
      <span className="admin-price-bucket-label">{name}</span>
      <span className="admin-price-bucket-value">{formatPrice(bucket)}</span>
      {bucket && bucket.sample_size != null && (
        <span className="admin-price-bucket-sample">
          {bucket.sample_size} listing{bucket.sample_size === 1 ? "" : "s"}
        </span>
      )}
      {showEvidence && <EvidenceList evidence={bucket?.evidence} />}
    </div>
  );
}

function PriceRow({ price, showEvidence }) {
  if (!price) {
    return <span className="admin-status admin-status--inline">No price synced yet</span>;
  }
  return (
    <div className="admin-price-buckets">
      {BUCKETS.map((bucket) => (
        <PriceBucket key={bucket} name={bucket} bucket={price[bucket]} showEvidence={showEvidence} />
      ))}
    </div>
  );
}

function HistoryList({ history }) {
  if (history.length === 0) {
    return <p className="admin-status admin-status--inline">No history yet.</p>;
  }
  return (
    <div className="admin-history">
      {[...history].reverse().map((entry, i) => (
        <div className="admin-history-row" key={i}>
          <span className="admin-history-date">{new Date(entry.synced_at).toLocaleString()}</span>
          <PriceRow price={entry.price} showEvidence />
        </div>
      ))}
    </div>
  );
}

function PricingCard({ item, user, onForbidden, onToggleSync }) {
  const apiFetch = useAdminApi(user, onForbidden);
  const [expanded, setExpanded] = useState(false);
  const [history, setHistory] = useState(null);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [togglingSync, setTogglingSync] = useState(false);

  async function toggleHistory() {
    if (expanded) {
      setExpanded(false);
      return;
    }
    setExpanded(true);
    if (history) return;
    setLoadingHistory(true);
    try {
      const data = await apiFetch(`/admin/pricing/${item.pricing_id}/history`);
      setHistory(data.history);
    } finally {
      setLoadingHistory(false);
    }
  }

  async function handleToggleSync() {
    setTogglingSync(true);
    try {
      await onToggleSync(item.pricing_id, !!item.sync_disabled);
    } finally {
      setTogglingSync(false);
    }
  }

  return (
    <div className="admin-card glass">
      <div className="admin-card-header">
        <div className="admin-card-barcode">
          {item.name || "Unlinked product"}
          {item.platform && <span className="admin-source-meta"> · {item.platform}</span>}
          {item.barcodes && item.barcodes.length > 0 && (
            <span className="admin-source-meta">
              {" · "}
              {item.barcodes.map((b) => <code className="admin-barcode-chip" key={b}>{b}</code>)}
            </span>
          )}
        </div>
        <button
          type="button"
          className={`admin-signout-btn admin-sync-toggle-btn${item.sync_disabled ? " admin-sync-toggle-btn--disabled" : ""}`}
          onClick={handleToggleSync}
          disabled={togglingSync}
          title={item.sync_disabled ? "Not sold on eBay / stopped burning quota — click to re-enable" : "Stop this item from auto-resyncing"}
        >
          {togglingSync ? "…" : item.sync_disabled ? "Sync disabled" : "Sync enabled"}
        </button>
      </div>

      <PriceRow price={item.latest_price?.price} />
      {item.latest_price && (
        <div className="admin-source-meta admin-price-synced">
          Last synced {new Date(item.latest_price.synced_at).toLocaleString()}
        </div>
      )}

      <button className="admin-signout-btn admin-history-toggle" onClick={toggleHistory}>
        {expanded ? "Hide history" : "View history"}
      </button>

      {expanded && (
        loadingHistory
          ? <p className="admin-status admin-status--inline">Loading history…</p>
          : <HistoryList history={history || []} />
      )}
    </div>
  );
}

const PAGE_SIZE = 20;

function PricingTab({ user, onForbidden }) {
  const apiFetch = useAdminApi(user, onForbidden);
  const [items, setItems] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [nameInput, setNameInput] = useState("");
  const [platformInput, setPlatformInput] = useState("");
  const [name, setName] = useState("");
  const [platform, setPlatform] = useState("");
  const [noPrice, setNoPrice] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);

  // Debounce the search inputs so it doesn't fire a request on every keystroke —
  // resets back to page 1 since the result set changes.
  useEffect(() => {
    const handle = setTimeout(() => {
      setName(nameInput.trim());
      setPlatform(platformInput.trim());
      setPage(1);
    }, 350);
    return () => clearTimeout(handle);
  }, [nameInput, platformInput]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({ page: String(page), page_size: String(PAGE_SIZE) });
      if (name) params.set("name", name);
      if (platform) params.set("platform", platform);
      if (noPrice) params.set("no_price", "true");
      const data = await apiFetch(`/admin/pricing?${params.toString()}`);
      setItems(data.items);
      setTotalPages(data.total_pages || 1);
      setTotal(data.total || 0);
    } catch (err) {
      if (err.message !== "Forbidden") setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [apiFetch, page, name, platform, noPrice]);

  useEffect(() => {
    load();
  }, [load]);

  async function toggleSync(pricingId, currentlyDisabled) {
    await apiFetch(`/admin/pricing/${pricingId}/sync-enabled`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ enabled: currentlyDisabled }),
    });
    load();
  }

  function handleNoPriceChange(e) {
    setNoPrice(e.target.checked);
    setPage(1);
  }

  return (
    <div>
      <p className="kicker">eBay listings, mapped per product</p>
      <h2 className="admin-title admin-title--left">Pricing</h2>

      <div className="admin-links-search">
        <input
          type="text"
          placeholder="Search by name…"
          value={nameInput}
          onChange={(e) => setNameInput(e.target.value)}
          className="admin-tgdb-link-input admin-links-search-input"
        />
        <input
          type="text"
          placeholder="Search by platform…"
          value={platformInput}
          onChange={(e) => setPlatformInput(e.target.value)}
          className="admin-tgdb-link-input admin-links-search-input"
        />
        <label className="admin-checkbox-label">
          <input type="checkbox" checked={noPrice} onChange={handleNoPriceChange} />
          No pricing in all versions
        </label>
      </div>

      {loading && <p className="admin-status">Loading…</p>}
      {error && <p className="admin-status admin-error">{error}</p>}

      <div className="admin-links">
        {items && items.length === 0 && <p className="admin-status">No pricing data found.</p>}
        {items && items.map((item) => (
          <PricingCard key={item.pricing_id} item={item} user={user} onForbidden={onForbidden} onToggleSync={toggleSync} />
        ))}
      </div>

      {items && items.length > 0 && (
        <div className="admin-pagination">
          <button
            type="button"
            className="admin-signout-btn"
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page <= 1 || loading}
          >
            Previous
          </button>
          <span className="admin-pagination-status">
            Page {page} of {totalPages} · {total} total
          </span>
          <button
            type="button"
            className="admin-signout-btn"
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page >= totalPages || loading}
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
}

export default PricingTab;
